import { useMemo } from "react";
import { ColumnDef, FilterFn, Row, SortingFn } from "@tanstack/react-table";
import { InfoIcon, ArrowUpRightIcon, Inbox } from "lucide-react";
import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { AvatarImage } from "@radix-ui/react-avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Creator, Campaign } from "@/types/types";
import { EvePipelinePhase } from "@/types/eve";
import {
  multiColumnFilterFn,
  statusFilterFn,
  issuesFilterFn,
} from "../filters/filterFunctions";
import {
  ColumnFilterMeta,
  dateRangeFilterFn,
  numberRangeFilterFn,
  selectFilterFn,
  textFilterFn,
} from "../filters/columnFilters";
import { AGE_UNKNOWN, ageKey, ageLabel, countryName, genderLabel, languageLabel } from "./labels";
import { resolvePhaseState } from "../phase/phaseState";
import { useCreatorsTableContext, useCreatorsTableState } from "../CreatorsTableContext";
import SelectColumnCell from "./SelectColumnCell";
import UsernameColumnCell from "./UsernameColumnCell";
import FollowersColumnCell from "./FollowersColumnCell";
import AvgViewsColumnCell from "./AvgViewsColumnCell";
import MedianViewsColumnCell from "./MedianViewsColumnCell";
import StatusColumnCell from "./StatusColumnCell";
import PriceColumnCell from "./PriceColumnCell";
import TierColumnCell from "./TierColumnCell";
import DynamicFieldColumnCell from "./DynamicFieldColumnCell";
import EditFieldDescriptionDialog from "./EditFieldDescriptionDialog";
import CountryColumnCell from "./CountryColumnCell";
import GenderColumnCell from "./GenderColumnCell";
import LanguageColumnCell from "./LanguageColumnCell";
import CampaignColumnCell from "./CampaignColumnCell";
import InboxColumnCell, { creatorInbox } from "./InboxColumnCell";
import PhaseColumnCell from "../phase/PhaseColumnCell";
import { BadgeStatus } from "@/components/dashboard_campaigns/status-badges";
import { CopyButton } from "@/components/ui/copy-button";
import { Input } from "@/components/ui/input";
import { isEveCampaign, evePhaseOrder, evePhaseLabel, allPhases, pipelineForCreator } from "@/lib/eve";
import { resolveStatusItem } from "@/constants/statuses";
import { UnionField, unionCampaignFields } from "./unionFields";
import { formatCompact } from "@/lib/format";

interface UseCreatorColumnsOptions {
  onRefresh: () => Promise<void>;
  /** The open campaign (campaign table); null org-wide. */
  campaign: Campaign | null;
  /** Every campaign rows may belong to — [campaign] in a campaign table, the whole org in the Global CRM. */
  campaigns: Campaign[];
  /** The campaign a row belongs to (see CreatorsTableContextValue.campaignFor). */
  campaignFor: (creator: Creator | null | undefined) => Campaign | null;
  /**
   * Org-wide Global CRM table: adds the Campaign + Inbox columns, merges
   * campaign-specific columns (CRM fields, pipeline phases, stage fields)
   * across campaigns, and resolves per-row context through campaignFor.
   */
  orgWide?: boolean;
  /** Public share / read-only mode: drops sensitive columns + the table context. */
  readOnly?: boolean;
  /** Read-only: show performance metric columns (followers, views, eng. rate, country). */
  showStats?: boolean;
  /** Read-only: show collected CRM field columns + the status badge column. */
  showFields?: boolean;
  /** Read-only: show a per-creator inbox button that opens the conversation panel. */
  showConversations?: boolean;
}

// Context-free creator cell for public share pages — avatar + @username + a link
// to the creator's external profile, plus an optional read-only inbox button.
function ReadOnlyUsernameCell({
  creator,
  showConversations,
}: {
  creator: Creator;
  showConversations?: boolean;
}) {
  const { setSelectedCreatorId } = useCreatorsTableContext();
  const { selectedCreatorId } = useCreatorsTableState();
  const { username, platform, platform_id, avatar, thread_id } = creator;
  const profileLink =
    platform === "tiktok"
      ? `https://www.tiktok.com/@${platform_id}`
      : platform === "youtube"
        ? `https://www.youtube.com/channel/${platform_id}`
        : `https://www.instagram.com/${username}`;

  return (
    <div className="flex items-center gap-2 w-full pr-2">
      <Link
        to={profileLink}
        target="_blank"
        rel="noopener noreferrer"
        className="group flex items-center gap-2 w-fit max-w-90 h-11 pl-1 pr-2 rounded-md hover:bg-muted-foreground/15 transition-all relative"
      >
        <Avatar className="size-9.5 border bg-muted p-0.25">
          <AvatarImage src={avatar} alt="Avatar" className="rounded-pill" />
          <AvatarFallback>
            {username?.charAt(0)?.toUpperCase()}
            {username?.charAt(1)?.toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <span className="max-w-52 truncate font-medium text-sm">@{username}</span>
        <ArrowUpRightIcon className="shrink-0 size-3 opacity-10 group-hover:opacity-100 group-hover:translate-x-0.75 group-hover:-translate-y-0.75 transition-all text-foreground" />
      </Link>
      {showConversations && thread_id && (
        <Button
          className={`shrink-0 size-8 ml-auto text-muted-foreground hover:bg-muted-foreground/10 ${creator._id === selectedCreatorId
            ? "ring-3 ring-primary/30 border-primary"
            : ""
            }`}
          size="icon"
          variant="outline"
          title="View conversation"
          onClick={() => setSelectedCreatorId(creator._id)}
        >
          <Inbox />
        </Button>
      )}
    </div>
  );
}

// Read-only display of a collected CRM field value (no editing).
function ReadOnlyFieldCell({
  creator,
  field,
}: {
  creator: Creator;
  field: { name: string; type: string };
}) {
  const entry = creator.fields?.find((f) => f.name === field.name);
  const value = entry?.value;
  let label: string;
  if (field.type === "boolean") {
    label =
      entry?.accepted === true
        ? "Accepted"
        : entry?.accepted === false
          ? "Rejected"
          : value || "—";
  } else {
    label = value || "—";
  }
  return <span className="text-sm text-muted-foreground">{label}</span>;
}

// The two cells that show the open-panel highlight read the panel id here,
// in a thin wrapper, and hand their memoized cell a boolean — so opening a
// thread re-renders two cells, not every cell in the column.
function SelectCell({ row }: { row: Row<Creator> }) {
  const { selectedCreatorId } = useCreatorsTableState();
  return (
    <SelectColumnCell
      creatorId={row.original._id}
      isSelected={row.getIsSelected()}
      panelSelected={row.original._id === selectedCreatorId}
      onToggleSelected={row.toggleSelected}
    />
  );
}

function StatusCell({
  creator,
  status,
  onRefresh,
  campaign,
}: {
  creator: Creator;
  status: string;
  onRefresh: () => Promise<void>;
  campaign: Campaign | null;
}) {
  const { loadingStatusCreators } = useCreatorsTableState();
  return (
    <StatusColumnCell
      creator={creator}
      status={status}
      loadingThisCreator={loadingStatusCreators[creator._id]}
      onRefresh={onRefresh}
      campaign={campaign}
    />
  );
}

function UsernameCell({ creator }: { creator: Creator }) {
  const { selectedCreatorId } = useCreatorsTableState();
  return <UsernameColumnCell creator={creator} selected={creator._id === selectedCreatorId} />;
}

/**
 * The row's campaign never defined this column (a field, phase or stage
 * another campaign has) — or, for a phase column, the row's TIER pipeline
 * lacks the phase (`title` names that case).
 */
function NotInCampaignCell({ what, title }: { what: string; title?: string }) {
  return (
    <span
      className="text-xs text-muted-foreground/60 italic pl-2"
      title={title ?? `This campaign has no "${what}"`}
    >
      —
    </span>
  );
}

const numericSort = (
  rowA: { getValue: (id: string) => unknown },
  rowB: { getValue: (id: string) => unknown },
  columnId: string
) => {
  const a = rowA.getValue(columnId) as number | undefined;
  const b = rowB.getValue(columnId) as number | undefined;
  const isAInvalid =
    a === undefined || a === null || typeof a !== "number" || isNaN(a);
  const isBInvalid =
    b === undefined || b === null || typeof b !== "number" || isNaN(b);
  if (isAInvalid && isBInvalid) return 0;
  if (isAInvalid) return 1;
  if (isBInvalid) return -1;
  return a - b;
};

/**
 * A CRM column's cell value as the table sees it — sorting and the column
 * filter read this, since the entry lives in creator.fields, not on the
 * creator itself.
 */
function crmFieldValue(
  creator: Creator,
  field: UnionField
): string | number | undefined {
  const entry = creator.fields?.find((f) => f.name === field.name);
  const value = entry?.value ?? "";
  if (field.type === "boolean") {
    // Mirrors DynamicFieldColumnCell: a decision needs a value behind it.
    if (entry?.accepted === true && value !== "") return "accepted";
    if (entry?.accepted === false && value !== "") return "rejected";
    return "missing";
  }
  if (field.type === "number") {
    if (value === "") return undefined;
    const n = Number(value);
    return Number.isNaN(n) ? undefined : n;
  }
  return value;
}

const CRM_BOOLEAN_LABELS: Record<string, string> = {
  accepted: "Accepted",
  rejected: "Rejected",
  missing: "Missing",
};

interface CrmColumnFilter {
  filterFn: FilterFn<Creator>;
  sortingFn?: SortingFn<Creator>;
  meta: { filter: ColumnFilterMeta };
}

/** Filter editor + sort for a CRM column, by the field's type. */
function crmColumnFilter(field: UnionField): CrmColumnFilter {
  if (field.type === "boolean") {
    return {
      filterFn: selectFilterFn,
      meta: {
        filter: {
          kind: "select",
          label: field.name,
          format: (value) => CRM_BOOLEAN_LABELS[value] ?? value,
          // Org-wide the loaded page can't enumerate these — offer them always.
          options: ["accepted", "rejected", "missing"],
        },
      },
    };
  }
  if (field.type === "number") {
    return {
      filterFn: numberRangeFilterFn,
      sortingFn: numericSort,
      meta: { filter: { kind: "number", label: field.name } },
    };
  }
  if (field.type === "date") {
    return {
      filterFn: dateRangeFilterFn,
      meta: { filter: { kind: "date", label: field.name } },
    };
  }
  return {
    filterFn: textFilterFn,
    meta: { filter: { kind: "text", label: field.name, placeholder: "Contains…" } },
  };
}

const PAID_COMPONENT_TYPES = ["max_budget", "max_cpm", "custom_formula"];

function hasPaidTier(campaign: Campaign | null | undefined): boolean {
  return !!campaign?.partnership?.tiers?.some((tier) =>
    tier.components.some((c) => PAID_COMPONENT_TYPES.includes(c.type))
  );
}

/** Does the row's campaign define this field? Org-wide a merged column can be foreign to a row. */
function campaignHasField(campaign: Campaign | null, name: string): boolean {
  return !!campaign?.fields?.some((f) => f.name === name);
}

const STATUS_COLUMN_MIN = 150;
const STATUS_COLUMN_MAX = 410;

/**
 * Fit the Status column to the widest status actually in the data (long
 * labels made the fixed 410 necessary, but short ones wasted the space).
 * Width is estimated (~8px/char + allowances for what StatusColumnCell
 * renders around the badge) and clamped; a few px off just truncates the
 * badge label, which keeps its title tooltip. CreatorsTable writes it into
 * the table's columnSizing state.
 */
export function useStatusColumnSize(
  data: Creator[],
  campaignFor: (creator: Creator | null | undefined) => Campaign | null
): number {
  return useMemo(() => {
    let widest = 0;
    for (const c of data) {
      if (!c.status) continue;
      const rowCampaign = campaignFor(c);
      const stages = isEveCampaign(rowCampaign) ? undefined : rowCampaign?.stages;
      const label =
        resolveStatusItem(c.status, stages, pipelineForCreator(rowCampaign, c))?.name ??
        c.status;
      let w = 60 + label.length * 8; // badge padding + icon + text
      if (c.status === "waiting-approval") w += 80; // approve/reject buttons
      else if (stages?.some((s) => s.id === c.status)) w += 96; // stage advance/countdown
      if (c.hand_off_to_user) w += 36; // warning icon
      if (c.eve_contract_change?.status === "pending") w += 36; // contract-change icon
      if (c.error) w += 36; // error icon
      widest = Math.max(widest, w);
    }
    return Math.min(STATUS_COLUMN_MAX, Math.max(STATUS_COLUMN_MIN, widest));
  }, [data, campaignFor]);
}

export function useCreatorColumns({
  onRefresh,
  campaign,
  campaigns,
  campaignFor,
  orgWide = false,
  readOnly = false,
  showStats = false,
  showFields = false,
  showConversations = false,
}: UseCreatorColumnsOptions): ColumnDef<Creator>[] {
  // In read-only mode, metric columns only appear when stats sharing is enabled.
  const showMetrics = !readOnly || showStats;

  const campaignsById = useMemo(
    () => new Map(campaigns.map((c) => [c._id, c])),
    [campaigns]
  );
  // Newest first — the order the campaigns page and the sidebar use.
  const campaignOrder = useMemo(
    () =>
      [...campaigns]
        .sort(
          (a, b) =>
            new Date(b.creation_date).getTime() - new Date(a.creation_date).getTime()
        )
        .map((c) => c._id),
    [campaigns]
  );

  const baseColumns: ColumnDef<Creator>[] = useMemo(() => {
    const cols: ColumnDef<Creator>[] = [];
    const longFormYouTube =
      campaign?.platform === "youtube" && campaign.youtube_content_type === "long_form";
    const viewsTip = (kind: "Average" | "Median") =>
      orgWide
        ? `${kind} views on the creator's short-form videos (long-form videos for YouTube long-form campaigns)`
        : longFormYouTube
          ? `${kind} views on Long Form YouTube videos`
          : `${kind} views on the creator's short-form videos`;

    if (!readOnly) {
      cols.push(
        {
          id: "select",
          header: ({ table }) => (
            <Checkbox
              checked={
                table.getIsAllPageRowsSelected() ||
                (table.getIsSomePageRowsSelected() && "indeterminate")
              }
              onCheckedChange={(value) =>
                table.toggleAllPageRowsSelected(!!value)
              }
              aria-label="Select all"
              className="mt-0.5"
            />
          ),
          cell: ({ row }) => <SelectCell row={row} />,
          size: 28,
          enableSorting: false,
          enableHiding: false,
        },
        {
          id: "issues",
          accessorFn: (row) => row,
          cell: () => null,
          enableHiding: true,
          enableSorting: false,
          filterFn: issuesFilterFn,
        }
      );
    }

    cols.push({
      header: "Username",
      accessorKey: "username",
      cell: ({ row }) =>
        readOnly ? (
          <ReadOnlyUsernameCell
            creator={row.original}
            showConversations={showConversations}
          />
        ) : (
          <UsernameCell creator={row.original} />
        ),
      size: 350,
      filterFn: multiColumnFilterFn,
      enableHiding: false,
    });

    if (orgWide && !readOnly) {
      cols.push(
        {
          id: "campaign",
          header: "Campaign",
          // Keyed by campaign id so the checklist's keys match the server's
          // campaign facet; the label comes from `format`.
          accessorFn: (row) => row.campaign_id ?? "",
          size: 240,
          filterFn: selectFilterFn,
          meta: {
            filter: {
              kind: "select",
              label: "Campaign",
              format: (id) => (id ? campaignsById.get(id)?.campaign_name ?? id : ""),
              order: campaignOrder,
            },
          },
          cell: ({ row }) => <CampaignColumnCell campaign={campaignFor(row.original)} />,
        },
        {
          id: "inbox",
          header: "Inbox",
          accessorFn: (row) => creatorInbox(row, campaignFor(row))?.email ?? "",
          size: 260,
          filterFn: selectFilterFn,
          meta: { filter: { kind: "select", label: "Inbox" } },
          cell: ({ row }) => {
            const inbox = creatorInbox(row.original, campaignFor(row.original));
            return <InboxColumnCell email={inbox?.email ?? ""} provider={inbox?.provider ?? ""} />;
          },
        }
      );
    }

    if (showMetrics) {
      cols.push(
        {
          header: "Followers",
          accessorKey: "followers",
          size: 120,
          sortingFn: numericSort,
          filterFn: numberRangeFilterFn,
          meta: { filter: { kind: "number", label: "Followers" } },
          cell: ({ row }) => (
            <FollowersColumnCell
              followers={row.getValue("followers") as number | undefined}
            />
          ),
        },
        {
          header: () => (
            <Tooltip delayDuration={300}>
              <TooltipTrigger className="cursor-help">Avg Views</TooltipTrigger>
              <TooltipContent className="max-w-46 wrap-break-word text-center">
                {viewsTip("Average")}
              </TooltipContent>
            </Tooltip>
          ),
          accessorKey: "average_views",
          size: 121,
          sortingFn: numericSort,
          filterFn: numberRangeFilterFn,
          meta: { filter: { kind: "number", label: "Avg Views" } },
          cell: ({ row }) => (
            <AvgViewsColumnCell
              averageViews={row.getValue("average_views") as number | undefined}
            />
          ),
        },
        {
          header: () => (
            <Tooltip delayDuration={300}>
              <TooltipTrigger className="cursor-help">Median Views</TooltipTrigger>
              <TooltipContent className="max-w-46 wrap-break-word text-center">
                {viewsTip("Median")}
              </TooltipContent>
            </Tooltip>
          ),
          accessorKey: "median_views",
          size: 146,
          sortingFn: numericSort,
          filterFn: numberRangeFilterFn,
          meta: { filter: { kind: "number", label: "Median Views" } },
          cell: ({ row }) => (
            <MedianViewsColumnCell
              medianViews={row.getValue("median_views") as number | undefined}
            />
          ),
        },
        {
          header: "Avg Likes",
          accessorKey: "avg_likes",
          size: 110,
          sortingFn: numericSort,
          filterFn: numberRangeFilterFn,
          meta: { filter: { kind: "number", label: "Avg Likes" } },
          cell: ({ row }) => (
            <div className="text-muted-foreground font-mono">
              {formatCompact(row.getValue("avg_likes") as number | undefined)}
            </div>
          ),
        },
        {
          header: "Eng Rate",
          accessorKey: "engagement_rate",
          size: 120,
          sortingFn: numericSort,
          filterFn: numberRangeFilterFn,
          // Stored 0–1, shown as a percentage — the filter talks percent too.
          meta: { filter: { kind: "number", label: "Eng Rate", scale: 100, unit: "%" } },
          cell: ({ row }) => {
            const value = row.getValue("engagement_rate") as number | undefined;
            return (
              <div className="text-muted-foreground font-mono">
                {value != null ? `${(value * 100).toFixed(1)}%` : "-"}
              </div>
            );
          },
        },
        {
          header: "Country",
          accessorKey: "country",
          size: 140,
          filterFn: selectFilterFn,
          meta: { filter: { kind: "select", label: "Country", format: countryName } },
          cell: ({ row }) => (
            <CountryColumnCell country={row.getValue("country") as string} />
          ),
        },
        {
          id: "gender",
          header: "Gender",
          accessorKey: "creator_gender",
          size: 100,
          filterFn: selectFilterFn,
          meta: { filter: { kind: "select", label: "Gender", format: genderLabel } },
          cell: ({ row }) => (
            <GenderColumnCell gender={row.getValue("gender") as string | undefined} />
          ),
        },
        {
          id: "age",
          header: "Age",
          accessorFn: (row) => ageKey(row.creator_age),
          size: 100,
          filterFn: selectFilterFn,
          meta: {
            // Also hides it for visibility choices stored before the column existed.
            defaultHidden: true,
            filter: { kind: "select", label: "Age", format: ageLabel },
          },
          cell: ({ row }) => {
            const age = row.getValue("age") as string;
            // Mirrors GenderColumnCell: not determined and missing are a dash.
            return !age || age === AGE_UNKNOWN ? (
              <div
                className="text-[13px] pr-2 text-muted-foreground"
                title={age ? "Not determined" : undefined}
              >
                -
              </div>
            ) : (
              <div className="text-[13px] pr-2 truncate leading-5">{ageLabel(age)}</div>
            );
          },
        },
        {
          id: "language",
          header: "Language",
          accessorKey: "lang_iso",
          size: 110,
          filterFn: selectFilterFn,
          meta: { filter: { kind: "select", label: "Language", format: languageLabel } },
          cell: ({ row }) => (
            <LanguageColumnCell langIso={row.getValue("language") as string | undefined} />
          ),
        }
      );
    }

    if (!readOnly) {
      cols.push({
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => (
          <StatusCell
            creator={row.original}
            status={row.getValue("status") as string}
            onRefresh={onRefresh}
            campaign={campaignFor(row.original)}
          />
        ),
        // The real width is data-driven and lives in the table's columnSizing
        // state (useStatusColumnSize) — keeping it out of the definition keeps
        // the columns (and every mounted cell) stable across page flips.
        size: STATUS_COLUMN_MIN,
        filterFn: statusFilterFn,
      });
    } else if (showFields) {
      // Read-only status badge — shown alongside the collected field columns.
      cols.push({
        header: "Status",
        accessorKey: "status",
        size: 220,
        cell: ({ row }) => (
          <BadgeStatus
            status={row.getValue("status") as string}
            stages={campaign?.stages}
            pipeline={pipelineForCreator(campaign, row.original)}
          />
        ),
      });
    }

    return cols;
  }, [
    onRefresh,
    campaign,
    campaignFor,
    campaignsById,
    campaignOrder,
    orgWide,
    readOnly,
    showFields,
    showMetrics,
    showConversations,
  ]);

  const priceColumn: ColumnDef<Creator> | null = useMemo(() => {
    if (readOnly) return null;
    if (orgWide) {
      // Org-wide: shown as soon as any campaign pays a fee; each row formats
      // in its own campaign's currency.
      if (!campaigns.some(hasPaidTier)) return null;
    } else {
      const hasOneTier =
        campaign?.partnership?.tiers && campaign.partnership.tiers.length === 1;
      if (!hasPaidTier(campaign) || !hasOneTier) return null;
    }

    return {
      accessorKey: "negotiated_price",
      size: 130,
      sortingFn: numericSort,
      filterFn: numberRangeFilterFn,
      meta: {
        filter: {
          kind: "number",
          label: "Price",
          unit: orgWide ? undefined : campaign?.currency || undefined,
        },
      },
      header: () => (
        <Tooltip delayDuration={0}>
          <TooltipTrigger className="flex flex-row items-center gap-2">
            <span>Price</span>
            <InfoIcon className="size-3.5" />
          </TooltipTrigger>
          <TooltipContent className="text-center">
            <p>Negotiated price from Amy for the deliverables.</p>
            <p>CPM = price ÷ (median views × deliverables) × 1000</p>
          </TooltipContent>
        </Tooltip>
      ),
      cell: ({ row }) => {
        const rowCampaign = campaignFor(row.original);
        return (
          <PriceColumnCell
            negotiatedPrice={row.getValue("negotiated_price") as string}
            currency={rowCampaign?.currency}
            medianViews={row.original.median_views}
            deliverables={rowCampaign?.number_of_deliverables}
          />
        );
      },
    };
  }, [campaign, campaigns, campaignFor, orgWide, readOnly]);

  const tierColumn: ColumnDef<Creator> | null = useMemo(() => {
    if (readOnly) return null;
    // Multi-tier campaigns — and Amy campaigns where the team sets creators'
    // offers (the cell carries them).
    const showsTier = (c: Campaign | null | undefined) =>
      (c?.partnership?.tiers?.length ?? 0) > 1 || c?.eve_offer_review === true;
    if (orgWide ? !campaigns.some(showsTier) : !showsTier(campaign)) return null;

    return {
      accessorKey: "current_tier_order",
      // Fits "Paid: $2000 ($40 CPM) + 20% Rev Share"; longer summaries truncate.
      size: 300,
      filterFn: selectFilterFn,
      meta: {
        filter: {
          kind: "select",
          label: "Current Tier",
          format: (value) => (value ? `Tier ${value}` : ""),
        },
      },
      header: () => (
        <Tooltip delayDuration={300}>
          <TooltipTrigger className="cursor-help">Current Tier</TooltipTrigger>
          <TooltipContent className="wrap-break-word text-center">
            Current tier in discussions with Amy.
          </TooltipContent>
        </Tooltip>
      ),
      cell: ({ row }) => {
        const rowCampaign = campaignFor(row.original);
        return (
          <TierColumnCell
            currentTierOrder={row.original.current_tier_order}
            tiers={rowCampaign?.partnership?.tiers}
            status={row.getValue("status") as string}
            negotiatedPrice={row.original.negotiated_price as unknown as string}
            currency={rowCampaign?.currency}
            offer={row.original.eve_offer_overrides?.find((o) => o.tier_order === (row.original.current_tier_order ?? 1))}
          />
        );
      },
    };
  }, [campaign, campaigns, campaignFor, orgWide, readOnly]);

  // One cell per enabled Amy-v2 pipeline phase (progress-row model): dash
  // before, ✓ + date after, phase-specific state + dialog actions while
  // current. Hidden in share mode (no dialogs/actions there, v1). Columns are
  // the UNION of phases by KEY across every Amy-run campaign in scope AND
  // across each campaign's partnership tiers (allPhases — a phase SOME tier
  // runs gets a column); the first pipeline carrying the key names it. Each
  // cell reads the entry of ITS row's tier pipeline — a row whose tier lacks
  // or disables the phase shows "—" (NotInCampaignCell).
  const evePhaseColumns: ColumnDef<Creator>[] = useMemo(() => {
    if (readOnly) return [];
    const eveCampaigns = campaigns
      .filter((c) => isEveCampaign(c))
      .map((c) => ({ campaign: c, union: allPhases(c) }))
      .filter((c) => c.union.length > 0);
    if (eveCampaigns.length === 0) return [];

    const phases = new Map<string, { phase: EvePipelinePhase; union: EvePipelinePhase[] }>();
    for (const { union } of eveCampaigns) {
      for (const phase of union) {
        if (phase.enabled === true && !phases.has(phase.key)) {
          phases.set(phase.key, { phase, union });
        }
      }
    }

    return Array.from(phases.values())
      .sort((a, b) => evePhaseOrder(a.phase) - evePhaseOrder(b.phase))
      .map(({ phase, union }) => {
        const label = evePhaseLabel(phase, union);
        const entryFor = (creator: Creator) =>
          pipelineForCreator(campaignFor(creator), creator).find(
            (p) => p.key === phase.key && p.enabled === true
          ) ?? null;
        return {
          id: `phase_${phase.key}`,
          // State label so the column filter can list phases as a checklist;
          // sorting by it would be alphabetical noise, so that stays off.
          accessorFn: (row: Creator) => {
            const entry = entryFor(row);
            return entry ? resolvePhaseState(row, entry).label : "";
          },
          enableSorting: false,
          filterFn: selectFilterFn,
          // Phase states aren't a server-side filter; org-wide the columns
          // start hidden and stay out of the filter popover.
          meta: orgWide
            ? { defaultHidden: true }
            : {
                filter: {
                  kind: "select" as const,
                  label,
                },
              },
          header: () => (
            <Tooltip delayDuration={300}>
              <TooltipTrigger className="cursor-help">{label}</TooltipTrigger>
              <TooltipContent className="text-center max-w-72 w-auto">
                <p>
                  {phase.shape ? `${phase.shape} step` : `${phase.key} phase`}
                  {phase.instructions ? ` — ${phase.instructions}` : ""}
                </p>
              </TooltipContent>
            </Tooltip>
          ),
          size: 220,
          cell: ({ row }: { row: { original: Creator } }) => {
            const rowCampaign = campaignFor(row.original);
            const entry = entryFor(row.original);
            return entry ? (
              <PhaseColumnCell creator={row.original} phase={entry} campaign={rowCampaign} />
            ) : (
              <NotInCampaignCell what={`${label} phase`} title="Not in this tier's pipeline" />
            );
          },
        };
      });
  }, [campaigns, campaignFor, orgWide, readOnly]);

  // CRM field columns — the campaign's own fields, or org-wide the merge of
  // every campaign's fields by name (unionCampaignFields).
  const unionFields = useMemo(() => unionCampaignFields(campaigns), [campaigns]);

  const dynamicColumns: ColumnDef<Creator>[] = useMemo(() => {
    // Hidden in read-only unless collected-fields sharing is enabled.
    if (readOnly && !showFields) return [];
    if (!unionFields.length) return [];

    return unionFields.map((field) => ({
      header: () =>
        readOnly || orgWide ? (
          <Tooltip delayDuration={300}>
            <TooltipTrigger className="cursor-help">{field.name}</TooltipTrigger>
            <TooltipContent className="text-center max-w-72 w-auto">
              <p>{field.description || field.name}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <div className="flex-1 flex flex-row items-center gap-1 group/header">
            <Tooltip delayDuration={300}>
              <TooltipTrigger className="flex flex-row items-center gap-2 hover:bg-muted h-7.5 px-2 rounded-md">
                <span>{field.name}</span>
                <InfoIcon className="size-3.5" />
              </TooltipTrigger>
              <TooltipContent className="text-center max-w-72 w-auto">
                <p>{field.description}</p>
              </TooltipContent>
            </Tooltip>
            {campaign && (
              <EditFieldDescriptionDialog
                fieldName={field.name}
                currentDescription={field.description}
                allFields={campaign.fields ?? []}
                campaignId={campaign._id}
              />
            )}
          </div>
        ),
      id: field.name,
      accessorFn: (row: Creator) => crmFieldValue(row, field),
      ...crmColumnFilter(field),
      size: 250,
      cell: ({ row }) => {
        if (readOnly) return <ReadOnlyFieldCell creator={row.original} field={field} />;
        // Org-wide: a merged column can belong to another campaign — never
        // offer an editor that would push a field the campaign doesn't collect.
        if (orgWide && !campaignHasField(campaignFor(row.original), field.name)) {
          return <NotInCampaignCell what={field.name} />;
        }
        return <DynamicFieldColumnCell creator={row.original} field={field} />;
      },
    }));
  }, [unionFields, campaign, campaignFor, orgWide, readOnly, showFields]);

  // One read-only column per stage that collects a field. The collected value
  // lives at creator.stage_info.data[collect_field] (keyed by the stage's slug).
  // Org-wide the stages merge by collect_field across legacy campaigns.
  const stageCollectedColumns: ColumnDef<Creator>[] = useMemo(() => {
    // Hidden in read-only unless collected-fields sharing is enabled.
    if (readOnly && !showFields) return [];

    const collected = new Map<string, { id: string; name: string }>();
    for (const c of campaigns) {
      if (isEveCampaign(c)) continue;
      for (const stage of c.stages ?? []) {
        if (stage.collect_field && !collected.has(stage.collect_field)) {
          collected.set(stage.collect_field, { id: stage.id, name: stage.name });
        }
      }
    }
    if (collected.size === 0) return [];

    return Array.from(collected, ([collectField, stage]) => ({
      id: `stage_collected_${stage.id}`,
      header: stage.name,
      accessorFn: (row: Creator) => row.stage_info?.data?.[collectField] ?? "",
      filterFn: textFilterFn,
      meta: orgWide
        ? { defaultHidden: true }
        : { filter: { kind: "text" as const, label: stage.name } },
      size: 250,
      cell: ({ row }: { row: { original: Creator } }) => {
        const value = row.original.stage_info?.data?.[collectField];
        return value ? (
          <div className="flex items-center justify-between group">
            <Input
              className="w-full rounded-r-none border-r-0 shadow-none placeholder:italic placeholder:text-xs border-transparent bg-transparent group-hover:bg-background group-hover:border-border pr-0 group-hover:shadow-xs"
              value={value}
              readOnly
              placeholder="missing"
            />
            <CopyButton
              buttonText=""
              tooltipText={`Copy ${collectField}`}
              textToCopy={value}
              variant="outline"
              className="opacity-0 group-hover:opacity-100 size-9 rounded-l-none border-l-0 border-border group-hover:shadow-xs"
              tooltipDelay={750}
            />
          </div>
        ) : (
          <span className="text-xs text-muted-foreground italic pl-2">
            missing
          </span>
        );
      },
    }));
  }, [campaigns, orgWide, readOnly, showFields]);

  return useMemo(() => {
    const allColumns = [...baseColumns];
    if (priceColumn) allColumns.push(priceColumn);
    if (tierColumn) allColumns.push(tierColumn);
    // Creator (CRM) field columns first, then the pipeline phases.
    allColumns.push(...dynamicColumns);
    allColumns.push(...evePhaseColumns);
    allColumns.push(...stageCollectedColumns);
    return allColumns;
  }, [baseColumns, priceColumn, tierColumn, dynamicColumns, evePhaseColumns, stageCollectedColumns]);
}
